/**
 * DatasetEditPage — single page for both create and edit modes.
 *
 * Routes: `/datasets/new` (create) and `/datasets/:datasetId/edit` (edit). The
 * shared DatasetForm handles dispatching to the right mutation.
 */

import { ArrowLeft } from 'lucide-react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { DatasetForm } from '@/components/datasets/DatasetForm'
import { Button } from '@/components/ui/button'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { Skeleton } from '@/components/ui/skeleton'
import { useDataset, type Dataset } from '@/hooks/useDatasets'

interface DatasetEditPageProps {
  mode: 'create' | 'edit'
}

export default function DatasetEditPage({
  mode,
}: DatasetEditPageProps): React.JSX.Element {
  const { datasetId } = useParams<{ datasetId: string }>()
  const navigate = useNavigate()
  const { data: dataset, isLoading } = useDataset(
    mode === 'edit' ? datasetId : undefined,
  )

  function handleSaved(saved: Dataset): void {
    toast.success(mode === 'create' ? 'Dataset created' : 'Dataset saved')
    navigate(`/datasets/${saved.id}`)
  }

  function handleCancel(): void {
    navigate(mode === 'edit' && datasetId ? `/datasets/${datasetId}` : '/datasets')
  }

  return (
    <PageContainer className="space-y-6">
      <Button variant="ghost" size="sm" asChild className="-ml-2">
        <Link
          to={mode === 'edit' && datasetId ? `/datasets/${datasetId}` : '/datasets'}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Link>
      </Button>

      <PageHeader
        title={mode === 'create' ? 'New dataset' : 'Edit dataset'}
        description={
          mode === 'create'
            ? 'Pick a slug and display name. You can add traces from any trace detail page.'
            : `Update ${dataset?.display_name ?? 'this dataset'}.`
        }
      />

      {/* The page is the standard width; the fields are not. Text inputs
          stretched across it read as a search bar, so the form keeps a
          form's width. */}
      <div className="max-w-2xl">
        {mode === 'edit' && isLoading && <Skeleton className="h-64 w-full" />}

        {(mode === 'create' || dataset) && (
          <DatasetForm
            mode={mode}
            initial={mode === 'edit' ? dataset : undefined}
            onSaved={handleSaved}
            onCancel={handleCancel}
          />
        )}
      </div>
    </PageContainer>
  )
}
